// Admin console: add a knowledge file or replace one (docs/contract.md 9.2).
// A workbench (design review ui.region_gate editor page):
//   header   plain spacing  back to the list, what is being written
//   editor   card           Nama berkas (new only), Pilih berkas .md (design review
//                           core.file_button: name and size shown, the text
//                           lands in the editor for review), the monospace text
//   check    card           Hasil pemeriksaan and the two actions (design review
//                           validate_flow check_then_save): Periksa isi runs
//                           POST /knowledge/validate; Simpan is enabled only
//                           while the current name and text passed; any edit
//                           marks the result stale. A parse error names its
//                           line and column and offers Ke baris N (design review skipped
//                           the line-number gutter at 0.50).
//   help     card           the JSON-in-.md format and a copyable minimal example
// From 1024 the check and help cards sit in an aside beside the editor.
import { useEffect, useRef, useState, type ChangeEvent } from "react";
import * as API from "../api";
import { formatCount } from "../lib/format";
import { navigate } from "../router";
import { Field, fieldAria, Notice, PageHeader, SkeletonRows, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";
import { fileHref } from "./Knowledge";
import { scrollWithin } from "../lib/scroll";
import {
  bytesOf,
  errorPosition,
  fileNameError,
  formatSize,
  isSystemFile,
  lineCount,
  MAX_BYTES,
  MINIMAL_EXAMPLE,
  offsetOf,
  suggestFileName,
  summaryFacts,
  SYSTEM_FILES,
} from "./knowledge-lib";

type Check =
  | { status: "idle" }
  | { status: "checking" }
  | { status: "ok"; key: string; summary: unknown }
  | { status: "error"; key: string; message: string };

const keyOf = (file: string, content: string) => `${file}\n${content}`;

export function KnowledgeEditorScreen({ file: existing }: { file?: string }) {
  const replacing = existing !== undefined;
  const system = replacing && isSystemFile(existing);
  useTitle(replacing ? `Ganti isi ${existing}` : "Tambah berkas");
  const [file, setFile] = useState(existing ?? "");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(replacing);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [picked, setPicked] = useState<{ name: string; size: number } | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [check, setCheck] = useState<Check>({ status: "idle" });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const editor = useRef<HTMLTextAreaElement>(null);
  const result = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!replacing) return;
    let alive = true;
    API.getKnowledgeFile(existing).then(
      (raw) => {
        if (!alive) return;
        setContent(raw.content);
        setLoading(false);
      },
      (err) => {
        if (!alive) return;
        setLoadError(API.errorMessage(err));
        setLoading(false);
      },
    );
    return () => {
      alive = false;
    };
  }, [existing, replacing]);

  const name = file.trim();
  const key = keyOf(name, content);
  const fresh = (check.status === "ok" || check.status === "error") && check.key === key;
  const stale = (check.status === "ok" || check.status === "error") && check.key !== key;
  const canSave = check.status === "ok" && fresh && !saving;
  const size = bytesOf(content);

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.currentTarget.files?.[0];
    e.currentTarget.value = "";
    if (!f) return;
    setPickError(null);
    if (!/\.md$/i.test(f.name)) {
      setPickError(`${f.name} bukan berkas .md. Hanya berkas Markdown (.md) yang diterima.`);
      return;
    }
    if (f.size > MAX_BYTES) {
      setPickError(`${f.name} berukuran ${formatSize(f.size)}, melebihi batas 2 MB.`);
      return;
    }
    const text = await f.text();
    setContent(text);
    setContentError(null);
    setPicked({ name: f.name, size: f.size });
    if (!replacing && !file.trim()) {
      setFile(suggestFileName(f.name));
      setNameError(null);
    }
  }

  async function runCheck() {
    setSaveError(null);
    const nErr = replacing ? null : fileNameError(name);
    const cErr = !content.trim() ? "Isi berkas masih kosong. Pilih berkas .md atau tempel isinya." : size > MAX_BYTES ? `Isi berkas ${formatSize(size)}, melebihi batas 2 MB.` : null;
    setNameError(nErr);
    setContentError(cErr);
    if (nErr || cErr) {
      document.getElementById(nErr ? "kb-file" : "kb-content")?.focus();
      return;
    }
    const at = key;
    setCheck({ status: "checking" });
    try {
      const res = await API.validateKnowledge(name, content);
      setCheck({ status: "ok", key: at, summary: res.summary });
    } catch (err) {
      setCheck({ status: "error", key: at, message: API.errorMessage(err) });
    }
    scrollWithin(result.current);
  }

  async function save() {
    if (!canSave) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (replacing) await API.replaceKnowledgeFile(name, content);
      else await API.createKnowledgeFile(name, content);
      navigate(`${fileHref(name)}?disimpan=1`);
    } catch (err) {
      if (err instanceof API.ApiError && err.status === 422) setCheck({ status: "error", key, message: err.message });
      else if (err instanceof API.ApiError && err.status === 409) setSaveError(`Berkas ${name} sudah ada. Pakai nama lain, atau buka berkas itu dari daftar lalu pilih Ganti isi.`);
      else setSaveError(API.errorMessage(err));
      setSaving(false);
    }
  }

  function goToLine(line: number, column: number) {
    const el = editor.current;
    if (!el) return;
    const offset = offsetOf(content, line, column);
    el.focus();
    el.setSelectionRange(offset, offset);
    const lh = Number.parseFloat(getComputedStyle(el).lineHeight) || 20;
    el.scrollTop = Math.max(0, (line - 3) * lh);
  }

  const position = check.status === "error" ? errorPosition(check.message) : null;
  const sysMeta = system ? SYSTEM_FILES[existing] : null;

  return (
    <>
      <PageHeader
        back={{ href: replacing ? fileHref(existing) : "/admin/pengetahuan", label: replacing ? existing : "Basis Pengetahuan" }}
        title={replacing ? `Ganti isi ${sysMeta?.label ?? existing}` : "Tambah berkas pengetahuan"}
        lead={
          replacing
            ? "Isi baru diperiksa dulu, lalu menggantikan isi lama. Versi lama disimpan di riwayat server."
            : "Unggah berkas .md atau tempel isinya. Isi diperiksa dulu sebelum disimpan."
        }
      />

      <div className="kb-workbench">
        <section className="card kb-editor" aria-labelledby="kb-editor-title">
          <h2 id="kb-editor-title" className="card-title">
            Berkas
          </h2>
          {loadError ? (
            <Notice tone="danger" title="Isi lama belum dapat dimuat">
              <p>{loadError}</p>
            </Notice>
          ) : null}

          {replacing ? (
            <dl className="facts kb-file-facts">
              <div className="fact">
                <dt>Nama berkas</dt>
                <dd className="mono">{existing}</dd>
              </div>
            </dl>
          ) : (
            <Field id="kb-file" label="Nama berkas" required error={nameError} hint="Huruf kecil, angka, dan tanda hubung, diakhiri .md. Contoh: perpres-16-2018.md">
              <input
                {...fieldAria("kb-file", nameError)}
                className="mono-input"
                value={file}
                spellCheck={false}
                autoCapitalize="none"
                autoComplete="off"
                placeholder="nama-berkas.md"
                onChange={(e) => {
                  setFile(e.currentTarget.value);
                  setNameError(null);
                }}
              />
            </Field>
          )}

          <div className="field">
            <span className="field-label" id="kb-pick-label">
              Unggah berkas
            </span>
            <div className="kb-pick">
              <input ref={picker} type="file" accept=".md,text/markdown" hidden onChange={onPick} aria-labelledby="kb-pick-label" />
              <button type="button" className="btn btn-secondary" onClick={() => picker.current?.click()} disabled={loading}>
                <Glyph name="documentDownload" />
                <span>{picked ? "Pilih berkas lain" : "Pilih berkas .md"}</span>
              </button>
              <p className="kb-pick-text" aria-live="polite">
                {picked ? (
                  <>
                    <span className="mono">{picked.name}</span>
                    <span className="num">{formatSize(picked.size)}</span>
                  </>
                ) : (
                  <span>Atau tempel isinya di kolom di bawah.</span>
                )}
              </p>
            </div>
            <p className="field-hint" data-state={pickError ? "error" : undefined} aria-live="polite">
              {pickError ? (
                <>
                  <Glyph name="danger" size={16} />
                  <span>{pickError}</span>
                </>
              ) : (
                "Hanya .md, maksimal 2 MB. Isi berkas tampil di bawah untuk diperiksa dulu."
              )}
            </p>
          </div>

          <Field
            id="kb-content"
            label="Isi berkas"
            required
            error={contentError}
            hint={`${formatCount(lineCount(content))} baris, ${formatSize(size)} dari maksimal 2 MB`}
          >
            {loading ? (
              <SkeletonRows count={6} label="Memuat isi lama" />
            ) : (
              <textarea
                {...fieldAria("kb-content", contentError)}
                ref={editor}
                className="md-editor kb-textarea"
                value={content}
                spellCheck={false}
                autoCapitalize="none"
                wrap="off"
                placeholder={"```json\n{\n  \"id\": \"...\",\n  \"title\": \"...\",\n  \"sections\": []\n}\n```"}
                onChange={(e) => {
                  setContent(e.currentTarget.value);
                  setContentError(null);
                }}
              />
            )}
          </Field>
        </section>

        <aside className="kb-aside" aria-label="Pemeriksaan dan panduan format">
          <section ref={result} className="card kb-check" aria-labelledby="kb-check-title">
            <h2 id="kb-check-title" className="card-title">
              Hasil pemeriksaan
            </h2>
            <div className="kb-check-state" aria-live="polite">
              {check.status === "checking" ? (
                <p className="quiet-line">
                  <Glyph name="clock" />
                  <span>Memeriksa format berkas</span>
                </p>
              ) : stale ? (
                <Notice tone="info" title="Isi berubah setelah diperiksa">
                  <p>Periksa ulang sebelum menyimpan.</p>
                </Notice>
              ) : check.status === "ok" ? (
                <Notice tone="success" title="Format berkas valid">
                  <p>Isi dapat dibaca dan siap disimpan.</p>
                  {summaryFacts(check.summary).length ? (
                    <dl className="kb-summary">
                      {summaryFacts(check.summary).map((f) => (
                        <div key={f.label} className="kb-summary-row">
                          <dt>{f.label}</dt>
                          <dd>{f.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                </Notice>
              ) : check.status === "error" ? (
                <Notice
                  tone="danger"
                  title="Format berkas belum sesuai"
                  action={
                    position ? (
                      <button type="button" className="btn btn-secondary" onClick={() => goToLine(position.line, position.column)}>
                        Ke baris {position.line}
                      </button>
                    ) : undefined
                  }
                >
                  <p>{check.message}</p>
                </Notice>
              ) : (
                <p className="quiet-line">
                  <Glyph name="infoCircle" />
                  <span>Belum diperiksa. Periksa isi dulu, lalu simpan.</span>
                </p>
              )}
              {saveError ? (
                <Notice tone="danger" title="Berkas belum tersimpan">
                  <p>{saveError}</p>
                </Notice>
              ) : null}
            </div>
            <div className="button-row kb-check-actions">
              <button
                type="button"
                className={canSave ? "btn btn-secondary" : "btn"}
                onClick={runCheck}
                aria-busy={check.status === "checking" || undefined}
                disabled={loading || saving}
              >
                <Glyph name="shieldTick" />
                <span>{fresh ? "Periksa ulang" : "Periksa isi"}</span>
              </button>
              <button type="button" className={canSave ? "btn" : "btn btn-secondary"} onClick={save} disabled={!canSave} aria-busy={saving || undefined}>
                <Glyph name="tickCircle" />
                <span>Simpan</span>
              </button>
            </div>
          </section>

          <FormatHelp system={sysMeta?.kind ?? null} />
        </aside>
      </div>
    </>
  );
}

function FormatHelp({ system }: { system: API.SystemFileKind | null }) {
  const [copied, setCopied] = useState<"idle" | "done" | "failed">("idle");
  async function copy() {
    try {
      await navigator.clipboard.writeText(MINIMAL_EXAMPLE);
      setCopied("done");
    } catch {
      setCopied("failed");
    }
  }
  if (system) {
    return (
      <section className="card kb-help" aria-labelledby="kb-help-title">
        <h2 id="kb-help-title" className="card-title">
          Format berkas sistem
        </h2>
        <ul className="kb-rules">
          <li>Isi tetap satu objek JSON di dalam satu blok ```json; teks di luar blok diabaikan.</li>
          {system === "catalog" ? (
            <li>Katalog berisi daftar tahap, jenis dokumen, sub kategori, dan kolom formulir. Tahap dan jenis dokumen tidak boleh ganda.</li>
          ) : system === "elearning" ? (
            <li>Berkas E-Learning berisi kolom courses: setiap kelompok video dengan id, title, description, roles, dan lessons (id, title, youtubeId, durationMinutes, summary, source). Lebih mudah diubah dari halaman E-Learning.</li>
          ) : (
            <li>Berkas FAQ berisi kolom items, maksimal 50 butir, masing-masing dengan id unik, question, dan answer.</li>
          )}
          <li>Ubah dari isi yang ada. Bila pemeriksaan gagal, isi lama tetap dipakai.</li>
          <li>Penjelasan setiap kolom ada di knowledge/README.md.</li>
        </ul>
      </section>
    );
  }
  return (
    <section className="card kb-help" aria-labelledby="kb-help-title">
      <h2 id="kb-help-title" className="card-title">
        Format berkas
      </h2>
      <ul className="kb-rules">
        <li>Isi berkas adalah satu objek JSON, mentah atau di dalam satu blok ```json. Catatan di luar blok diabaikan.</li>
        <li>Wajib: id (kode unik), title, dan sections, minimal satu bagian dengan heading dan content.</li>
        <li>Opsional: category (regulasi, template, panduan, contoh), tahap, documentTypes, tags, source.</li>
        <li>Pecah dokumen panjang menjadi bagian pendek per pasal atau topik agar mudah dirujuk.</li>
      </ul>
      <pre className="raw-text kb-example" tabIndex={0} aria-label="Contoh berkas minimal">
        {MINIMAL_EXAMPLE}
      </pre>
      <div className="button-row kb-help-actions">
        <button type="button" className="btn btn-secondary" onClick={copy}>
          <Glyph name={copied === "done" ? "check" : "documentText"} />
          <span>{copied === "done" ? "Contoh tersalin" : "Salin contoh"}</span>
        </button>
        {copied === "failed" ? (
          <p className="kb-copy-status" role="status">
            Tidak dapat menyalin otomatis. Pilih teks contoh lalu salin.
          </p>
        ) : null}
      </div>
    </section>
  );
}
